import { useEffect, useState } from 'react';
import { Field } from '@sitecore-content-sdk/nextjs';
import { useRouter } from 'next/router';
import { OpenSchedulingItemType } from 'lib/hooks/open-scheduling-wizard/open-scheduling-types';

type TemplateProps = {
  item?: {
    epicDeptID?: Field<string> | null;
    epicPhysicianID?: Field<string> | null;
    name?: string;
    locationName?: Field<string> | null;
    providerTitle?: Field<string> | null;
  };
};

/**
 * Fetches the Epic context (`epicID` and `typeOfItem`) required to load the MyChart component.
 *
 * This hook is responsible for resolving the `itemId` and fetching its data.
 * It gives precedence to the directly provided `itemId` prop, falling back to
 * the query parameter `itemId`.
 *
 * ### Side Effects:
 * 1. **Internal Dependency:** Calls `useRouter()` to access router query params.
 * 2. **Fetches Data:** `useEffect` calls `getItem()` (GraphQL) when the ID changes.
 * 3. **Updates Context:** Calls `setItemName()` on success, updating the parent context.
 *
 **/
export const useEpicItemContext = (itemId: string | null, setItemName: (name: string) => void) => {
  const [epicID, setEpicID] = useState<string | null>(null);
  const [typeOfItem, setTypeOfItem] = useState<OpenSchedulingItemType>(null);

  const routerItemId = useRouter().query?.itemId;
  const queryableId = itemId || (typeof routerItemId === 'string' ? routerItemId : undefined);

  useEffect(() => {
    const fetchData = async () => {
      if (!queryableId) return;
      // ToDo: Centralize url building
      const base = process.env.PUBLIC_URL || process.env.NEXT_PUBLIC_URL;
      if (!base) return;
      // ToDo: Error handling for this GraphQL query? https://app.asana.com/1/9042223580080/project/1211036256662363/task/1211905214724518?focus=true
      const proxyData = await fetch(`${base}/api/get-appointment-item?path=${queryableId}`);
      const data = (await proxyData.json()) as TemplateProps;
      const deptID = data?.item?.epicDeptID?.value;
      if (deptID) {
        setEpicID(deptID);
        setItemName(data?.item?.locationName?.value || '');
        setTypeOfItem('location');
        return;
      }

      const epicPhysicianId = data?.item?.epicPhysicianID?.value;
      if (epicPhysicianId) {
        setEpicID(epicPhysicianId);
        setItemName(data?.item?.providerTitle?.value || '');
        setTypeOfItem('provider');
        return;
      }

      // ToDo: Is this actually correct for default? Does FluShot end up here? https://app.asana.com/1/9042223580080/project/1211036256662363/task/1211905214724518?focus=true
      setEpicID(null);
      setItemName('');
      setTypeOfItem('location');
    };

    fetchData();
  }, [queryableId, setItemName]);

  return {
    epicID,
    typeOfItem,
  };
};
